/**
 * AppShell — authenticated layout wrapping every admin route.
 *
 * Top header band (56px min-height — matches LoginCard for chrome consistency
 * per BUG-021 same-band envelope): dark depth gradient with the wordmark left
 * (onDepth variant: white with terracotta accent on "Talenta") and
 * LanguageChips + LogOut icon + HamburgerMenu right. A 3px terracotta bottom
 * border carries the brand identity into every admin surface (BUG-041 v2 —
 * depth band + warm accent rule, three-layer composition Eva approved
 * 2026-05-08 from Option C mockup at .planning/design-options/2026-05-08/
 * option-C-terracotta-with-depth.html).
 *
 * The header LogOut icon is the primary always-visible logout affordance
 * (Eva-UAT S-01, 2026-05-08 — drawer-only logout failed discoverability).
 * The HamburgerMenu drawer keeps a redundant logout button as backup; both
 * call the shared useLogout hook so the flow is single-sourced.
 */
import { LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Outlet } from "react-router";

import { LanguageChips } from "@/components/brand/LanguageChips";
import { Wordmark } from "@/components/brand/Wordmark";
import { Button } from "@/components/ui/button";
import { useLogout } from "@/features/auth/useLogout";
import { TOKENS } from "@/lib/tokens";

import { HamburgerMenu } from "./HamburgerMenu";

export function AppShell() {
  const { t } = useTranslation(["auth"]);
  const onLogout = useLogout();
  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: TOKENS.surface }}
    >
      <header
        className="sticky top-0 z-10 flex flex-row items-center justify-between px-4 w-full"
        style={{
          minHeight: "56px",
          // iOS notch / status-bar safe area: header background extends to true
          // viewport top; inner content gets pushed below the safe-area zone.
          // Requires `viewport-fit=cover` in index.html (added 2026-05-26).
          paddingTop: "env(safe-area-inset-top, 0px)",
          backgroundImage: `linear-gradient(135deg, ${TOKENS.depth} 0%, ${TOKENS.depthElevated} 100%)`,
          color: TOKENS.depthFg,
          borderBottom: `3px solid ${TOKENS.primary}`,
          boxShadow: TOKENS.shadow,
        }}
      >
        <div className="mr-3 sm:mr-0">
          <Wordmark size="md" variant="onDepth" stackOnMobile />
        </div>
        <div className="flex flex-row items-center gap-2 sm:gap-3">
          <LanguageChips />
          <Button
            type="button"
            variant="ghost"
            onClick={onLogout}
            aria-label={t("auth:logout")}
            title={t("auth:logout")}
            style={{
              minHeight: "44px",
              minWidth: "44px",
              color: TOKENS.depthFg,
            }}
          >
            <LogOut aria-hidden="true" />
          </Button>
          <HamburgerMenu />
        </div>
      </header>
      <main className="flex-1 p-6">
        <Outlet />
      </main>
    </div>
  );
}
